import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Camera, CameraOff, Loader2, Package, AlertTriangle,
  Flashlight, FlashlightOff, SwitchCamera, X, Wifi, WifiOff,
  QrCode, Radio,
} from 'lucide-react'
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

type ScanState = 'idle' | 'scanning' | 'resolving' | 'error' | 'no-camera'
type ScanMode = 'nfc' | 'qr'

// Check Web NFC API availability
const isNFCSupported = 'NDEFReader' in window

export function QRScannerPage() {
  const navigate = useNavigate()
  const [mode, setMode] = useState<ScanMode>(isNFCSupported ? 'nfc' : 'qr')
  const [scanState, setScanState] = useState<ScanState>('idle')
  const [error, setError] = useState('')
  const [torchOn, setTorchOn] = useState(false)
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment')
  const [nfcReading, setNfcReading] = useState(false)
  const [recentScans, setRecentScans] = useState<Array<{ code: string; name: string; id: string; time: Date; type: 'NFC' | 'QR' }>>([])
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const nfcAbortRef = useRef<AbortController | null>(null)

  // ── Shared: resolve a tagCode to an asset ──────────────────────────────────

  const resolveTag = useCallback(async (tagCode: string, type: 'NFC' | 'QR') => {
    setScanState('resolving')

    if (mode === 'qr') {
      await stopQRScanner()
    }

    try {
      const { data } = await api.get(`/scan/${tagCode}`)
      setRecentScans((prev) => [
        { code: tagCode, name: data.asset?.name ?? 'Unknown', id: data.assetId, time: new Date(), type },
        ...prev.slice(0, 9),
      ])
      navigate(`/assets/${data.assetId}`)
    } catch {
      setError(`Tag could not be resolved to an asset. It may have been deactivated or removed.`)
      setScanState('error')
    }
  }, [navigate, mode]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── NFC / RFID scanning ────────────────────────────────────────────────────

  const startNFC = useCallback(async () => {
    if (!isNFCSupported) {
      setError('NFC is not supported on this device or browser. Use Chrome on Android, or switch to QR mode.')
      setScanState('no-camera')
      return
    }

    setScanState('scanning')
    setNfcReading(true)
    setError('')

    try {
      const ndef = new (window as any).NDEFReader()
      const controller = new AbortController()
      nfcAbortRef.current = controller

      await ndef.scan({ signal: controller.signal })

      ndef.addEventListener('reading', ({ serialNumber, message }: any) => {
        // Try to extract tagCode from NDEF records first
        let tagCode: string | null = null

        // NDEF URL record prefix table (first byte of data)
        const NDEF_URL_PREFIXES: Record<number, string> = {
          0x00: '', 0x01: 'http://www.', 0x02: 'https://www.', 0x03: 'http://',
          0x04: 'https://', 0x05: 'tel:', 0x06: 'mailto:',
        }

        if (message?.records) {
          for (const record of message.records) {
            let text = ''

            if (record.recordType === 'url') {
              // NDEF URL records use a prefix byte — decode properly
              const bytes = new Uint8Array(record.data.buffer || record.data)
              const prefix = NDEF_URL_PREFIXES[bytes[0]] ?? ''
              const rest = new TextDecoder().decode(bytes.slice(1))
              text = prefix + rest
            } else if (record.recordType === 'text') {
              const decoder = new TextDecoder()
              text = decoder.decode(record.data)
            } else {
              continue
            }

            // Check for EstateKey scan URL
            const scanMatch = text.match(/\/scan\/([a-f0-9-]+)/i)
            if (scanMatch) {
              tagCode = scanMatch[1]
              break
            }

            // Check for raw UUID
            const uuidMatch = text.match(/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i)
            if (uuidMatch) {
              tagCode = uuidMatch[0]
              break
            }
          }
        }

        // Fall back to serial number (common for RFID tags)
        if (!tagCode && serialNumber) {
          // Format serial number as a lookup key (remove colons, lowercase)
          tagCode = serialNumber.replace(/:/g, '').toLowerCase()
        }

        if (tagCode) {
          resolveTag(tagCode, 'NFC')
        } else {
          setError('This NFC tag does not contain an EstateKey asset identifier.')
          setScanState('error')
        }
      }, { signal: controller.signal })

      ndef.addEventListener('readingerror', () => {
        setError('Could not read NFC tag. Try holding your device closer.')
        setScanState('error')
      }, { signal: controller.signal })

    } catch (err: any) {
      if (err?.name === 'AbortError') return // User navigated away
      if (err?.name === 'NotAllowedError') {
        setError('NFC permission denied. Please allow NFC access in your browser settings.')
      } else {
        setError('Unable to start NFC reader. Make sure NFC is enabled on your device.')
      }
      setScanState('no-camera')
      setNfcReading(false)
    }
  }, [resolveTag])

  const stopNFC = useCallback(() => {
    if (nfcAbortRef.current) {
      nfcAbortRef.current.abort()
      nfcAbortRef.current = null
    }
    setNfcReading(false)
  }, [])

  // ── QR camera scanning ─────────────────────────────────────────────────────

  const stopQRScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState()
        if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
          await scannerRef.current.stop()
        }
      } catch {
        // ignore
      }
    }
  }, [])

  const parseQRPayload = useCallback((decodedText: string): string => {
    // Extract tagCode from URL
    const scanMatch = decodedText.match(/\/scan\/([a-f0-9-]+)/i)
    if (scanMatch) return scanMatch[1]

    // Direct UUID
    const uuidMatch = decodedText.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i)
    if (uuidMatch) return uuidMatch[0]

    return decodedText
  }, [])

  const startQRScanner = useCallback(async () => {
    if (!containerRef.current) return

    setScanState('scanning')
    setError('')

    try {
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode('qr-reader')
      } else {
        await stopQRScanner()
      }

      await scannerRef.current.start(
        { facingMode },
        { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1 },
        (decodedText) => resolveTag(parseQRPayload(decodedText), 'QR'),
        () => { /* no QR in frame */ }
      )
    } catch (err: any) {
      if (err?.message?.includes('NotAllowedError') || err?.name === 'NotAllowedError') {
        setError('Camera access denied. Please allow camera access in your browser settings.')
      } else if (err?.message?.includes('NotFoundError') || err?.name === 'NotFoundError') {
        setError('No camera found on this device.')
      } else {
        setError('Unable to start camera. Please check your permissions.')
      }
      setScanState('no-camera')
    }
  }, [facingMode, resolveTag, stopQRScanner, parseQRPayload])

  // ── Mode switching ─────────────────────────────────────────────────────────

  const switchMode = useCallback(async (newMode: ScanMode) => {
    // Stop current scanner
    if (mode === 'qr') await stopQRScanner()
    if (mode === 'nfc') stopNFC()

    setMode(newMode)
    setScanState('idle')
    setError('')
  }, [mode, stopQRScanner, stopNFC])

  // Start scanner when mode changes
  useEffect(() => {
    if (scanState !== 'idle') return
    if (mode === 'nfc') startNFC()
    if (mode === 'qr') startQRScanner()
  }, [mode, scanState]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopQRScanner()
      stopNFC()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSwitchCamera = async () => {
    const newMode = facingMode === 'environment' ? 'user' : 'environment'
    setFacingMode(newMode)
    await stopQRScanner()
    setTimeout(() => startQRScanner(), 300)
  }

  const handleRetry = () => {
    setError('')
    setScanState('idle')
  }

  const toggleTorch = async () => {
    if (!scannerRef.current) return
    try {
      const capabilities = (scannerRef.current as any).getRunningTrackCameraCapabilities?.()
      if (capabilities?.torchFeature?.isSupported()) {
        await capabilities.torchFeature.apply(!torchOn)
        setTorchOn(!torchOn)
      }
    } catch {
      // Torch not supported
    }
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col safe-area-inset">
      {/* Header */}
      <header className="bg-navy-800 px-4 py-3 flex items-center gap-3 flex-shrink-0">
        <button
          onClick={() => navigate(-1)}
          className="text-white hover:text-gold-400 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
        <div className="flex-1">
          <h1 className="text-white font-semibold">Scan Asset Tag</h1>
          <p className="text-navy-300 text-xs">
            {mode === 'nfc' ? 'Hold device near NFC/RFID tag' : 'Point camera at QR code'}
          </p>
        </div>
        {mode === 'qr' && scanState === 'scanning' && (
          <div className="flex gap-2">
            <button
              onClick={toggleTorch}
              className={cn(
                'p-2 rounded-lg transition-colors',
                torchOn ? 'bg-gold-500 text-white' : 'text-navy-300 hover:text-white'
              )}
              title="Toggle flashlight"
            >
              {torchOn ? <FlashlightOff className="w-5 h-5" /> : <Flashlight className="w-5 h-5" />}
            </button>
            <button
              onClick={handleSwitchCamera}
              className="p-2 rounded-lg text-navy-300 hover:text-white transition-colors"
              title="Switch camera"
            >
              <SwitchCamera className="w-5 h-5" />
            </button>
          </div>
        )}
      </header>

      {/* Mode toggle tabs */}
      <div className="bg-navy-800 px-4 pb-3 flex-shrink-0">
        <div className="flex bg-navy-900 rounded-xl p-1 gap-1">
          <button
            onClick={() => mode !== 'nfc' && switchMode('nfc')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors',
              mode === 'nfc'
                ? 'bg-gold-500 text-white shadow-md'
                : isNFCSupported
                  ? 'text-navy-300 hover:text-white'
                  : 'text-navy-500 cursor-not-allowed'
            )}
            disabled={!isNFCSupported}
          >
            <Radio className="w-4 h-4" />
            NFC / RFID
          </button>
          <button
            onClick={() => mode !== 'qr' && switchMode('qr')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors',
              mode === 'qr'
                ? 'bg-gold-500 text-white shadow-md'
                : 'text-navy-300 hover:text-white'
            )}
          >
            <QrCode className="w-4 h-4" />
            QR Code
          </button>
        </div>
      </div>

      {/* Scanner viewport */}
      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        {/* QR camera reader (hidden when NFC mode) */}
        <div
          ref={containerRef}
          id="qr-reader"
          className={cn('w-full h-full max-w-lg', mode !== 'qr' && 'hidden')}
          style={{ minHeight: '300px' }}
        />

        {/* NFC scanning UI */}
        {mode === 'nfc' && scanState === 'scanning' && (
          <div className="flex flex-col items-center justify-center p-8">
            <div className="relative mb-8">
              {/* Pulsing rings */}
              <div className="absolute inset-0 w-40 h-40 rounded-full border-2 border-gold-500/20 animate-ping" style={{ animationDuration: '2s' }} />
              <div className="absolute inset-2 w-36 h-36 rounded-full border-2 border-gold-500/30 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }} />
              <div className="absolute inset-4 w-32 h-32 rounded-full border-2 border-gold-500/40 animate-ping" style={{ animationDuration: '2s', animationDelay: '1s' }} />
              {/* Center icon */}
              <div className="w-40 h-40 rounded-full bg-navy-800 border-2 border-gold-500 flex items-center justify-center relative z-10">
                <Radio className="w-16 h-16 text-gold-500" />
              </div>
            </div>
            <h2 className="text-white text-xl font-semibold mb-2">Ready to Scan</h2>
            <p className="text-gray-400 text-sm text-center max-w-xs">
              Hold the back of your device against the NFC/RFID tag on the asset
            </p>
            <div className="mt-6 flex items-center gap-2 text-gold-400 text-sm">
              <Wifi className="w-4 h-4 animate-pulse" />
              <span>NFC reader active</span>
            </div>
          </div>
        )}

        {/* QR scanning overlay */}
        {mode === 'qr' && scanState === 'scanning' && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="w-64 h-64 relative">
              <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-gold-500 rounded-tl-lg" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-gold-500 rounded-tr-lg" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-gold-500 rounded-bl-lg" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-gold-500 rounded-br-lg" />
              <div className="absolute left-4 right-4 h-0.5 bg-gold-500/80 animate-scan-line" />
            </div>
          </div>
        )}

        {/* Resolving state */}
        {scanState === 'resolving' && (
          <div className="absolute inset-0 bg-gray-900/80 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-10 h-10 text-gold-500 animate-spin mx-auto mb-3" />
              <p className="text-white font-medium">Looking up asset...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {(scanState === 'error' || scanState === 'no-camera') && (
          <div className="absolute inset-0 bg-gray-900/90 flex items-center justify-center p-6">
            <div className="text-center max-w-sm">
              {scanState === 'no-camera' ? (
                mode === 'nfc' ? (
                  <WifiOff className="w-12 h-12 text-red-400 mx-auto mb-4" />
                ) : (
                  <CameraOff className="w-12 h-12 text-red-400 mx-auto mb-4" />
                )
              ) : (
                <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              )}
              <h2 className="text-white font-semibold text-lg mb-2">
                {scanState === 'no-camera'
                  ? mode === 'nfc' ? 'NFC Unavailable' : 'Camera Unavailable'
                  : 'Tag Not Found'}
              </h2>
              <p className="text-gray-400 text-sm mb-6">{error}</p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleRetry}
                  className="bg-gold-500 hover:bg-gold-600 text-white font-medium px-6 py-2.5 rounded-xl transition-colors"
                >
                  Try Again
                </button>
                {scanState === 'no-camera' && mode === 'nfc' && (
                  <button
                    onClick={() => switchMode('qr')}
                    className="bg-navy-700 hover:bg-navy-600 text-white font-medium px-6 py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    <QrCode className="w-4 h-4" />
                    Switch to QR Scanner
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Idle state */}
        {scanState === 'idle' && (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="text-center">
              {mode === 'nfc' ? (
                <Radio className="w-12 h-12 text-navy-400 mx-auto mb-3" />
              ) : (
                <Camera className="w-12 h-12 text-navy-400 mx-auto mb-3" />
              )}
              <p className="text-gray-400 text-sm">
                {mode === 'nfc' ? 'Starting NFC reader...' : 'Starting camera...'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Recent scans */}
      {recentScans.length > 0 && (
        <div className="bg-navy-800 border-t border-navy-700 px-4 py-3 max-h-40 overflow-y-auto">
          <p className="text-navy-300 text-xs font-medium mb-2 uppercase tracking-wider">Recent Scans</p>
          <div className="space-y-1">
            {recentScans.map((scan, i) => (
              <button
                key={`${scan.id}-${i}`}
                onClick={() => navigate(`/assets/${scan.id}`)}
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-navy-700 transition-colors text-left"
              >
                <div className="w-7 h-7 rounded-lg bg-navy-700 flex items-center justify-center flex-shrink-0">
                  {scan.type === 'NFC' ? (
                    <Radio className="w-3.5 h-3.5 text-gold-500" />
                  ) : (
                    <QrCode className="w-3.5 h-3.5 text-gold-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{scan.name}</p>
                  <p className="text-navy-400 text-xs font-mono truncate">{scan.code}</p>
                </div>
                <span className="text-navy-500 text-xs flex-shrink-0">{scan.type}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bottom action */}
      <div className="bg-navy-800 border-t border-navy-700 p-4 flex-shrink-0">
        <button
          onClick={() => navigate('/assets')}
          className="w-full bg-navy-700 hover:bg-navy-600 text-white font-medium py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          <Package className="w-5 h-5" />
          Browse Assets
        </button>
      </div>
    </div>
  )
}
